/**
 * Les analyses disponibles.
 *
 * Ce qui s'appelait « les expériences » n'est plus la façade de l'application :
 * depuis le 10 août 2026, l'application est un monde, et ces pages sont ce qui
 * n'y est pas encore entré. Elles restent entières — ce sont les seules à
 * confronter des modèles à des mesures, et c'est la raison d'être du produit
 * (SPEC §2). Elles vivent dans un tiroir jusqu'à ce que le monde sache les
 * accueillir.
 *
 * Le bac à sable, lui, en est sorti par le haut : il **est** devenu le monde.
 */

import type { Experiment, ExperimentEntry } from "./types.js";
import { ERATOSTHENES } from "./eratosthenes.js";
import { FALL } from "./fall.js";
import { ORBIT } from "./orbit.js";
import { anyExperiment } from "./types.js";
import { experimentEntry } from "./page.js";

/**
 * Les expériences elles-mêmes — **la seule liste**.
 *
 * `EXPERIMENTS` en dérive au lieu de la recopier, et c'est tout l'intérêt :
 * une ligne ajoutée ici monte dans l'application *et* passe le contrat de
 * `tests/experiment.test.ts`. Deux listes tenues à la main auraient divergé le
 * jour où l'une des deux aurait été oubliée — et c'est l'oubli du côté des
 * tests qui ne se voit pas.
 */
export const EXPERIMENT_SPECS: readonly Experiment<unknown>[] = [
  anyExperiment(ERATOSTHENES),
  anyExperiment(FALL),
  anyExperiment(ORBIT),
];

export const EXPERIMENTS: readonly ExperimentEntry[] = EXPERIMENT_SPECS.map((one) =>
  experimentEntry(one),
);

/**
 * L'analyse mise en avant — **celle qui confronte**, et personne ne la désigne.
 *
 * Le tiroir rangeait les trois analyses au même niveau, derrière une pastille
 * qui disait « Analyses ». C'était le contraire de ce que SPEC §2 annonce :
 * l'unique chose à démontrer est qu'un modèle faux ne se sauve pas devant des
 * mesures, et une seule de ces pages sait recevoir des mesures. Nommer un
 * ensemble par sa catégorie enterrait la seule qui porte la thèse.
 *
 * Elle se **trouve** au lieu d'être choisie : la première du registre qui
 * accepte des mesures externes. Le jour où la chute libre recevra des relevés
 * chronométrés, elle deviendra candidate sans qu'on touche à l'interface — et
 * si demain plus aucune analyse ne confronte, l'entrée disparaît au lieu de
 * pointer vers une promesse vide.
 */
export const FEATURED_EXPERIMENT: ExperimentEntry | undefined = EXPERIMENTS.find(
  (one) => one.confronts,
);
